import React, { useState, useEffect } from "react";
import { Form, Select, Card, Row, Col, Button, message, Modal, Spin, Radio } from "antd";
import { connect } from "react-redux";
import { getUserList, getMenuList, getUserRoleId, getUserInfo, getClientid, getManagingOffice } from "redux/selectors";
import { CALL_API } from "common/API";
import { ExclamationCircleOutlined, CloseOutlined, CheckCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import dayjs from "dayjs";
import { borderRadius } from "@mui/system";
import { MAIN_CLIENT_ID } from "common/Constants";

const { Option } = Select;

const StyledModal = styled(Modal)`
  .modal-header-icon {
    font-size: 48px;
    color: red;
    display: block;
    margin: 0 auto 16px;
  }
  .modal-title {
    text-align: center;
    font-size: 18px;
    margin-bottom: 24px;
  }
  .created-text {
    font-size: 1.1rem;
    font-weight: bold;
    margin: 32px 0 24px 0;
    text-align: center;
  }
  // Center modal content
  .ant-modal-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 400px;
    min-height: 220px;
  }
`;

function ChangeManager({ userList, menuList, userRoleId, userInfo, clientId, managingOfficeList }) {
  const Menulist = menuList[userRoleId] || Object.values(menuList)[0];
  const canAssignManager = Menulist?.find(
    (item) =>
      item?.key === "manage" &&
      item?.subMenu.find(
        (subItem) =>
          subItem?.key === "changemanager" && subItem?.permission.edit
      )
  );
  console.log("canAssignManager", canAssignManager)
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pendingModalVisible, setPendingModalVisible] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [noPendingModalVisible, setNoPendingModalVisible] = useState(false);
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [pendingBillings, setPendingBillings] = useState([]);
  const [pendingEngagements, setPendingEngagements] = useState([]);
  const [changeType, setChangeType] = useState("partner"); // "partner" or "personIncharge"

  // Same relevant-user scoping as the Engagement module's Partner/Person
  // Incharge dropdowns (StakeHolderFields.js): office/subcluster match plus
  // a grade or manager-role check.
  const activeUserslist = userList?.filter((u) => u.statusid === "1") || [];
  const loggedInUser = activeUserslist?.find((u) => u.userid === userInfo?.userid);
  const partnerGrade = ["P1", "P2", "P3", "P4", "P5"];
  const MANAGER_ROLE_IDS = ["5", "4"];
  const isHeadOfficeUser = loggedInUser?.managingoffice?.[0] === "Head Office";

  // Default the Country filter to "Head Office" itself when it first shows.
  useEffect(() => {
    if (isHeadOfficeUser && form.getFieldValue("country") === undefined) {
      form.setFieldsValue({ country: "Head Office" });
    }
  }, [isHeadOfficeUser]);

  const managingOfficeOptions = (
    managingOfficeList?.includes("Head Office")
      ? managingOfficeList
      : ["Head Office", ...(managingOfficeList || [])]
  ).map((value) => ({
    label: value,
    value,
  }));

  // Head Office manages every country, so they pick which country to scope
  // Partner/Person Incharge to via the Country filter (no selection shows
  // everyone). Everyone else has no such filter - they're scoped to their
  // own managing office automatically.
  const selectedCountry = Form.useWatch("country", form);
  const scopeOffices = isHeadOfficeUser
    ? (selectedCountry ? [selectedCountry] : [])
    : loggedInUser?.managingoffice || [];

  const isOfficeMatch = (user) => {
    if (isHeadOfficeUser && scopeOffices.length === 0) return true;
    const isPrimaryOfficeMatch =
      Array.isArray(user?.managingoffice) &&
      user.managingoffice.some((office) => scopeOffices.includes(office));
    const isSubclusterMatch =
      user?.subcluster === true &&
      Array.isArray(user?.subclustercountries) &&
      user.subclustercountries.some((country) => scopeOffices.includes(country));
    return isPrimaryOfficeMatch || isSubclusterMatch;
  };

  const partnerUserOptions = activeUserslist.filter(
    (user) => isOfficeMatch(user) && partnerGrade.includes(user?.grade)
  );

  const managerUserOptions = activeUserslist.filter(
    (user) =>
      isOfficeMatch(user) &&
      Array.isArray(user?.roleid) &&
      user.roleid.some((id) => MANAGER_ROLE_IDS.includes(id))
  );

  // Mirrors StakeHolderFields.js: only the main client restricts Person
  // Incharge to the manager-grade/office-matched list; other clients see
  // all active users.
  const personInchargeUserOptions =
    clientId === MAIN_CLIENT_ID ? managerUserOptions : activeUserslist;

  const formatWeekRange = (weekStr) => {
    const [start, end] = weekStr.split("-");
    const fmt = (s) => dayjs(s, "YYYYMMMDD").format("DD MMM YYYY");
    return `${fmt(start)} – ${fmt(end)}`;
  };

  const handleSubmit = async () => {
    setLoading(true); // Ensure spinner starts
    try {
      const { partnerFrom, partnerTo, personInchargeFrom, personInchargeTo } =
        await form.validateFields();

      // Manual required validation
      if (changeType === "partner" && !partnerTo) {
        message.error("Partner To is required");
        setLoading(false);
        return;
      }
      if (changeType === "personIncharge" && !personInchargeTo) {
        message.error("Person Incharge To is required");
        setLoading(false);
        return;
      }

      // Build payload and query string based on changeType
      let pendingsPayload = {};
      let engagementPayload = {};
      let billingOrTimesheetPayload = {};
      let pendingsQS = "";
      let engagementQS = "";
      let billingOrTimesheetQS = "";

      if (changeType === "partner") {
        pendingsPayload = {
          oldPartner: partnerFrom,
          newPartner: partnerTo,
        };
        engagementPayload = { oldPartner: partnerFrom, newPartner: partnerTo };
        billingOrTimesheetPayload = { oldPartner: partnerFrom, newPartner: partnerTo };
        pendingsQS = `oldPartner=${partnerFrom || ""}&newPartner=${partnerTo || ""}`;
        engagementQS = pendingsQS;
        billingOrTimesheetQS = pendingsQS;
      } else if (changeType === "personIncharge") {
        pendingsPayload = {
          oldPersonIncharge: personInchargeFrom,
          newPersonIncharge: personInchargeTo,
        };
        engagementPayload = { oldPersonIncharge: personInchargeFrom, newPersonIncharge: personInchargeTo };
        billingOrTimesheetPayload = { oldPersonIncharge: personInchargeFrom, newPersonIncharge: personInchargeTo };
        pendingsQS = `oldPersonIncharge=${personInchargeFrom || ""}&newPersonIncharge=${personInchargeTo || ""}`;
        engagementQS = pendingsQS;
        billingOrTimesheetQS = pendingsQS;
      }

      const pendResp = await CALL_API(
        `insyts/engagement/pendingdatas?${pendingsQS}`,
        "patch",
        null
      );
      if (pendResp?.code !== "SUCCESS") {
        console.error("Pendings API error:", pendResp);
        message.error("Failed to check pending items.");
        setLoading(false);
        return;
      }

      const tsData = pendResp.timeSheetData || [];
      const billData = pendResp.billingData || [];
      const engData = pendResp.engagementData || [];

      if (tsData.length || billData.length || engData.length) {
        setPendingTimesheets(
          tsData?.map((item) => ({
            employeeName: item.employeeName,
            week: formatWeekRange(item.week),
          }))
        );
        setPendingBillings(
          billData?.map((b) => ({
            billingid: b.billingid,
            billingoffice: b.billingoffice,
            contactperson: b.contactperson,
          }))
        );
        setPendingEngagements(
          engData?.map((e) => ({
            engagementId: e.id,
            jobTitle: e?.information?.jobTitle,
          }))
        );
        setPendingModalVisible(true);
        setLoading(false);
        return;
      }

      // Nothing pending for the selected manager: nothing to move, so don't
      // run the update APIs or show the "Assigned Successfully" popup.
      setNoPendingModalVisible(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false); // Ensure spinner stops
    }
  };

  const handleOkClick = () => {
    setConfirmModalVisible(true);
  };

  const handleConfirmOk = async () => {
    setConfirmModalVisible(false);
    setLoading(true); // Ensure spinner starts
    try {
      const { partnerFrom, partnerTo, personInchargeFrom, personInchargeTo } =
        await form.validateFields();

      // Manual required validation
      if (changeType === "partner" && !partnerTo) {
        message.error("Partner To is required");
        setLoading(false);
        return;
      }
      if (changeType === "personIncharge" && !personInchargeTo) {
        message.error("Person Incharge To is required");
        setLoading(false);
        return;
      }

      // Build payload and query string based on changeType
      let engagementQS = "";
      let billingOrTimesheetQS = "";

      if (changeType === "partner") {
        engagementQS = `oldPartner=${partnerFrom || ""}&newPartner=${partnerTo || ""}`;
        billingOrTimesheetQS = engagementQS;
      } else if (changeType === "personIncharge") {
        engagementQS = `oldPersonIncharge=${personInchargeFrom || ""}&newPersonIncharge=${personInchargeTo || ""}`;
        billingOrTimesheetQS = engagementQS;
      }

      // Engagement API
      const engagementResp = await CALL_API(
        `insyts/engagement/engagement?${engagementQS}`,
        "patch",
        null
      );
      if (engagementResp?.code !== "SUCCESS") {
        message.error("Failed to update engagement.");
        setLoading(false);
        return;
      }

      // Billing or Timesheet API based on changeType
      if (changeType === "partner") {
        const billingResp = await CALL_API(
          `insyts/engagement/billing?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (billingResp?.code !== "SUCCESS") {
          message.error("Failed to update billing.");
          setLoading(false);
          return;
        }
      } else if (changeType === "personIncharge") {
        const timesheetResp = await CALL_API(
          `insyts/engagement/timesheet?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (timesheetResp?.code !== "SUCCESS") {
          message.error("Failed to update timesheet.");
          setLoading(false);
          return;
        }
      }

      setPendingModalVisible(false);
      form.resetFields();
      setIsModalVisible(true);
    } catch (err) {
      console.error(err);
      message.error(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false); // Ensure spinner stops
    }
  };

  const searchable = (name) => ({
    showSearch: true,
    allowClear: true,
    optionFilterProp: "children",
    filterOption: (inp, opt) =>
      opt.children.toLowerCase().includes(inp.toLowerCase()),
    filterSort: (a, b) =>
      a.children.toLowerCase().localeCompare(b.children.toLowerCase()),
    onClear: () => form.setFieldsValue({ [name]: undefined }),
    onChange: (val) => form.setFieldsValue({ [name]: val }),
  });

  // Helper to get employee names for modal message
  const getFromToNames = () => {
    let fromId, toId;
    if (changeType === "partner") {
      fromId = form.getFieldValue("partnerFrom");
      toId = form.getFieldValue("partnerTo");
    } else {
      fromId = form.getFieldValue("personInchargeFrom");
      toId = form.getFieldValue("personInchargeTo");
    }
    const fromUser = userList?.find(u => u.userid === fromId);
    const toUser = userList?.find(u => u.userid === toId);
    return {
      from: fromUser ? fromUser.fullname : "",
      to: toUser ? toUser.fullname : ""
    };
  };

  // Modified handleOkClick: directly start API calls and show loader
  const handlePendingOk = async () => {
    setLoading(true);
    try {
      const { partnerFrom, partnerTo, personInchargeFrom, personInchargeTo } =
        await form.validateFields();

      // Build payload and query string based on changeType
      let engagementQS = "";
      let billingOrTimesheetQS = "";

      if (changeType === "partner") {
        engagementQS = `oldPartner=${partnerFrom || ""}&newPartner=${partnerTo || ""}`;
        billingOrTimesheetQS = engagementQS;
      } else if (changeType === "personIncharge") {
        engagementQS = `oldPersonIncharge=${personInchargeFrom || ""}&newPersonIncharge=${personInchargeTo || ""}`;
        billingOrTimesheetQS = engagementQS;
      }

      // Engagement API
      const engagementResp = await CALL_API(
        `insyts/engagement/engagement?${engagementQS}`,
        "patch",
        null
      );
      if (engagementResp?.code !== "SUCCESS") {
        message.error("Failed to update engagement.");
        setLoading(false);
        return;
      }

      // Billing or Timesheet API based on changeType
      if (changeType === "partner") {
        const billingResp = await CALL_API(
          `insyts/engagement/billing?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (billingResp?.code !== "SUCCESS") {
          message.error("Failed to update billing.");
          setLoading(false);
          return;
        }
      } else if (changeType === "personIncharge") {
        const timesheetResp = await CALL_API(
          `insyts/engagement/timesheet?${billingOrTimesheetQS}`,
          "patch",
          null
        );
        if (timesheetResp?.code !== "SUCCESS") {
          message.error("Failed to update timesheet.");
          setLoading(false);
          return;
        }
      }

      setPendingModalVisible(false);
      form.resetFields();
      setIsModalVisible(true);
    } catch (err) {
      console.error(err);
      message.error(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Global page spinner overlay */}
      {loading && (
        <div
          style={{
            position: "fixed",
            zIndex: 2000,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(255,255,255,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column"
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 18, color: "#333" }}>
            Bulk Loading...
          </div>
        </div>
      )}

      <Card title="Change Manager">
        <Form form={form} layout="vertical">
          {/* Change type radio and, for Head Office users, the Country
              filter that scopes Partner/Person Incharge - on one line. */}
          <Row gutter={16} align="middle" justify="space-between" style={{ marginBottom: 24 }}>
            <Col>
              <Radio.Group
                value={changeType}
                onChange={e => {
                  setChangeType(e.target.value);
                  if (e.target.value === "partner") {
                    form.resetFields(["personInchargeFrom", "personInchargeTo"]);
                  } else if (e.target.value === "personIncharge") {
                    form.resetFields(["partnerFrom", "partnerTo"]);
                  }
                }}
              >
                <Radio value="partner">Partner</Radio>
                <Radio value="personIncharge">Person Incharge</Radio>
              </Radio.Group>
            </Col>
            {isHeadOfficeUser && (
              <Col flex="180px">
                <Form.Item name="country" style={{ marginBottom: 0 }}>
                  <Select
                    allowClear={false}
                    showSearch
                    placeholder="Select Country"
                    options={managingOfficeOptions}
                    onChange={() =>
                      form.resetFields([
                        "partnerFrom",
                        "partnerTo",
                        "personInchargeFrom",
                        "personInchargeTo",
                      ])
                    }
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          {/* Partner From / To */}
          {changeType === "partner" && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={("Partner From")}
                  name="partnerFrom"
                  rules={[
                    { required: true, message: "Partner From is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("partnerTo") === undefined ||
                          value !== getFieldValue("partnerTo")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Partner To and From cannot be the same")
                        );
                      },
                    }),
                  ]}
                >
                  <Select
                    {...searchable("partnerFrom")}
                    placeholder="Select Partner From"
                  >
                    {partnerUserOptions?.map((u) => (
                      <Option key={u.userid} value={u.userid}>
                        {u.fullname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={("Partner To")}
                  name="partnerTo"
                  rules={[
                    { required: true, message: "Partner To is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("partnerFrom") === undefined ||
                          value !== getFieldValue("partnerFrom")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Partner From and To cannot be the same")
                        );
                      },
                    }),
                  ]}
                >
                  <Select
                    {...searchable("partnerTo")}
                    placeholder="Select Partner To"
                  >
                    {partnerUserOptions?.map((u) => (
                      <Option key={u.userid} value={u.userid}>
                        {u.fullname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
          {/* Person In-Charge From / To */}
          {changeType === "personIncharge" && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={("Person Incharge From")}
                  name="personInchargeFrom"
                  rules={[
                    { required: true, message: "Person Incharge From is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("personInchargeTo") === undefined ||
                          value !== getFieldValue("personInchargeTo")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Person Incharge From and To cannot be the same")
                        );
                      },
                    }),
                  ]}
                >
                  <Select
                    {...searchable("personInchargeFrom")}
                    placeholder="Select Person Incharge From"
                  >
                    {personInchargeUserOptions?.map((u) => (
                      <Option key={u.userid} value={u.userid}>
                        {u.fullname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={("Person Incharge To")}
                  name="personInchargeTo"
                  rules={[
                    { required: true, message: "Person Incharge To is required" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (
                          !value ||
                          getFieldValue("personInchargeFrom") === undefined ||
                          value !== getFieldValue("personInchargeFrom")
                        ) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Person Incharge To and From cannot be the same")
                        );
                      },
                    }),
                  ]}
                >
                  <Select
                    {...searchable("personInchargeTo")}
                    placeholder="Select Person Incharge To"
                  >
                    {personInchargeUserOptions?.map((u) => (
                      <Option key={u.userid} value={u.userid}>
                        {u.fullname}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}
          {/* Submit */}
          <Row justify="end" style={{ marginTop: 16 }}>
            {canAssignManager && (
              <Button type="primary" onClick={handleSubmit} >
                Assign
              </Button>
            )}
          </Row>
        </Form>
      </Card>

      {/* Success Modal */}
      <StyledModal
        open={isModalVisible}
        footer={null}
        closable={false}
        centered
        bodyStyle={{ textAlign: "center" }}
        width={500}
      >
        <CheckCircleOutlined style={{ fontSize: 64, color: "#52c41a", marginBottom: 16 }} />
        <p className="created-text">Assigned Successfully!</p>
        <CloseOutlined
          style={{ position: "absolute", top: 16, right: 16, cursor: "pointer" }}
          onClick={() => {
            setIsModalVisible(false);
            form.resetFields(); // Reset form fields
          }}
        />
      </StyledModal>

      {/* No Pending Items Modal */}
      <StyledModal
        open={noPendingModalVisible}
        footer={null}
        closable={false}
        centered
        bodyStyle={{ textAlign: "center" }}
      >
        <ExclamationCircleOutlined className="modal-header-icon" />
        <p style={{ fontSize: "16px" }}>
          No pending items to update this manager.
        </p>
        <Button
          type="primary"
          onClick={() => setNoPendingModalVisible(false)}
          style={{ marginTop: 16 }}
        >
          OK
        </Button>
        <CloseOutlined
          style={{ position: "absolute", top: 16, right: 16, cursor: "pointer" }}
          onClick={() => setNoPendingModalVisible(false)}
        />
      </StyledModal>

      {/* Pending Items Modal */}
      <StyledModal
        open={pendingModalVisible}
        footer={null}
        closable={false}
        maskClosable={!loading}
        bodyStyle={{ textAlign: "center" }}
      >
        {(pendingTimesheets.length > 0 || pendingBillings.length > 0 || pendingEngagements.length > 0) ? (
          (() => {
            const { from, to } = getFromToNames();
            return (
              <>
                <ExclamationCircleOutlined className="modal-header-icon" />
                <p style={{ fontSize: "16px" }}>
                  Pending items are detected from the employee <b>{from}</b>
                  {pendingEngagements.length > 0 && (
                    <> ({pendingEngagements.length} engagement{pendingEngagements.length === 1 ? "" : "s"})</>
                  )}.
                  If you change to {to}, all the items will move to <b>{to}</b>.
                  If you agree, then click OK.
                </p>
                <Button
                  type="primary"
                  onClick={handlePendingOk}
                  style={{ marginTop: 16 }}
                  disabled={loading}
                >
                  OK
                </Button>
                <CloseOutlined
                  style={{ position: "absolute", top: 16, right: 16, cursor: "pointer" }}
                  onClick={() => !loading && setPendingModalVisible(false)}
                />
              </>
            );
          })()
        ) : (
          <>
          </>
        )}
      </StyledModal>
    </>
  );
}

const mapStateToProps = (state) => ({
  userList: getUserList(state),
  menuList: getMenuList(state),
  userRoleId: getUserRoleId(state),
  userInfo: getUserInfo(state),
  clientId: getClientid(state),
  managingOfficeList: getManagingOffice(state),
});

export default connect(mapStateToProps)(ChangeManager);
